/**
 * CNC export — the host-side naming and text rules (contract v66).
 *
 * Pure: no React, no DOM, no viewer. `scripts/check-cnc-filename.ts` runs it
 * headless.
 *
 * The VIEWER is the authority on every name and every byte of the program —
 * `exportBandCnc` returns `fileName`, `airCutFileName` and
 * `setupSheetFileName`, and the dialog always saves under those. What lives
 * here is what the host needs BEFORE a result exists (to show the file name the
 * user is about to get, and to refuse a model number the viewer would throw on)
 * and what it reads back OUT of a result (the size the viewer settled on, the
 * operations the program runs). The rules are copied from
 * `webgl-jewelry-viewer/src/band/cnc/generate.ts` (`HEADER_FIELD`, the
 * `<MODEL>S<SIZE>` base, `fmtSize`, `sizeOfRing`) and the check script guards
 * them against that source when the sibling repo is on disk.
 */
import { UK_RING_SIZES } from "../../../shared/api-contract";
import type { CncExportResult, CncSizeSystem } from "../../../shared/api-contract";

/** What the viewer accepts in a header field: letters, digits, ".", "_" and "-". */
export const CNC_HEADER_FIELD = /^[A-Za-z0-9._-]+$/;

/** The air-cut twin's file-name (and header label) suffix. */
export const CNC_AIR_CUT_SUFFIX = "-AIRCUT";

export const CNC_SIZE_SYSTEMS: readonly { id: CncSizeSystem; label: string }[] = [
  { id: "US", label: "US" },
  { id: "EU", label: "EU" },
  { id: "UK", label: "UK" },
  { id: "IN", label: "India" },
];

/**
 * Why a header field would be refused, in plain words — or null when it is fine.
 * `name` is how the dialog labels the field ("Model number", "Label").
 */
/**
 * Longest label or model (the viewer's `CNC_HEADER_FIELD_MAX`): line 1 must
 * still fit the controller once the air cut appends `-AIRCUT` to the label.
 */
export const CNC_HEADER_FIELD_MAX = 32;

export function headerFieldError(name: string, value: string): string | null {
  if (value.length === 0) return `${name} is needed — it goes on line 1 of the program and into the file name.`;
  if (value.length > CNC_HEADER_FIELD_MAX) return `${name} is ${value.length} characters — at most ${CNC_HEADER_FIELD_MAX}.`;
  if (/\s/.test(value)) return `${name} cannot contain spaces.`;
  if (!CNC_HEADER_FIELD.test(value)) return `${name} can only use letters, digits, ".", "_" and "-".`;
  return null;
}

/** A size number the way the viewer writes it: at most two decimals, no trailing zeros (10.75, 10.5, 11). */
export function formatCncSize(n: number): string {
  return String(+n.toFixed(2));
}

/** `<MODEL>S<SIZE>` with every "." of the size written as "-" (`368222S10-75`). */
export function cncFileBase(model: string, size: string): string {
  return `${model}S${size.replace(/\./g, "-")}`;
}

/** The files one export writes: the program, its air cut, the setup sheet and the Rhino reference. */
export function cncFileNames(model: string, size: string): { nc: string; airCut: string; setup: string; cam3dm: string } {
  const base = cncFileBase(model, size);
  return { nc: `${base}.NC`, airCut: `${base}${CNC_AIR_CUT_SUFFIX}.NC`, setup: `${base}-SETUP.txt`, cam3dm: `${base}.cam.3dm` };
}

/** The files the export dialog's footer names: the program, its air cut, its setup sheet and (when there is one) the .3dm. */
export interface CncDialogFileNames {
  nc: string;
  airCut: string;
  setup: string;
  cam3dm: string | null;
}

/**
 * What the dialog's footer lists. Once a check has come back, ONLY that
 * result's own names: its .3dm only when the result carries one — a check
 * that could not write it (rhino3dm unavailable, the writer failed) leaves the
 * .3dm button disabled, and the footer must not promise the file regardless.
 * The predicted names stand in only while nothing has been checked yet.
 */
export function cncDialogFileNames(
  current: Pick<CncExportResult, "fileName" | "airCutFileName" | "setupSheetFileName" | "cam3dm" | "cam3dmFileName"> | null,
  predicted: ReturnType<typeof cncFileNames> | null,
): CncDialogFileNames | null {
  if (!current) return predicted;
  return {
    nc: current.fileName,
    airCut: current.airCutFileName,
    setup: current.setupSheetFileName,
    cam3dm: current.cam3dm && current.cam3dmFileName ? current.cam3dmFileName : null,
  };
}

/**
 * Whether a `groove.shallow` warn names Z-follow as a fix. The viewer suggests
 * it only where the shortfall is across the face of a crowned blank with
 * Z-follow off; on the rounded edge every cut is held level with or without
 * it, so pointing at the switch there would send someone to a fix that does
 * not change the groove.
 */
export function grooveShallowSuggestsZFollow(message: string): boolean {
  return /What changes that: Z-follow in the machine profile/.test(message);
}

/**
 * The companion names of a program the viewer has already named
 * (`368222S10-75.NC` → its air cut, setup sheet and .3dm). A batch item
 * carries only `fileName`; the contract names the others from the same base.
 * Null when `fileName` is not a `.NC` name.
 */
export function cncCompanionNames(fileName: string): { airCut: string; setup: string; cam3dm: string } | null {
  if (!fileName.endsWith(".NC") || fileName.length === ".NC".length) return null;
  const base = fileName.slice(0, -".NC".length);
  return { airCut: `${base}${CNC_AIR_CUT_SUFFIX}.NC`, setup: `${base}-SETUP.txt`, cam3dm: `${base}.cam.3dm` };
}

/**
 * The size the viewer actually used, read back off the file name it returned
 * (`368222S10-75.NC` → `10.75`). Exact for every size the viewer derives
 * (digits, a decimal point, a UK letter) — none of them contain "-". Null when
 * the name is not `<model>S….NC`.
 */
export function sizeFromFileName(model: string, fileName: string): string | null {
  const head = `${model}S`;
  if (!fileName.startsWith(head) || !fileName.endsWith(".NC")) return null;
  const mid = fileName.slice(head.length, -".NC".length);
  if (mid.length === 0 || mid.endsWith(CNC_AIR_CUT_SUFFIX)) return null;
  return mid.replace(/-/g, ".");
}

/** What the host knows about the ring's size (all from `RingState`). */
export interface CncRingSize {
  ringSizeUS: number;
  ringSizeEU: number;
  /** `RingState.spec.innerDiameterMM` — the bore the ring is built at. */
  innerDiameterMM: number;
}

/**
 * The size the viewer will write when the export leaves `header.size` out —
 * the same rule as `generate.ts` `sizeOfRing`:
 *   US  the ring's own US size;
 *   EU  the ring's EU field, unless it disagrees with the bore's circumference
 *       (then the circumference, rounded);
 *   UK  the letter whose bore is nearest the ring's, a half written `.5`;
 *   IN  not predicted here (the Indian table lives in the viewer only) —
 *       `size` is null and the dialog shows the viewer's answer after the check.
 *
 * `note` is a whole sentence saying where the size came from, ready to show
 * as it is. Once a check has run, the viewer's own note replaces it
 * (`cncSizeSentence`).
 */
export function expectedCncSize(system: CncSizeSystem, ring: CncRingSize): { size: string | null; note: string } {
  const dia = ring.innerDiameterMM;
  const bore = `the ring's ${dia.toFixed(2)} mm bore`;
  switch (system) {
    case "US": {
      const size = formatCncSize(ring.ringSizeUS);
      return { size, note: `US ${size} is the ring's own size.` };
    }
    case "EU": {
      const eu = Math.round(Math.PI * dia);
      return eu === Math.round(ring.ringSizeEU)
        ? { size: formatCncSize(ring.ringSizeEU), note: `EU ${formatCncSize(ring.ringSizeEU)} is the ring's own size.` }
        : { size: String(eu), note: `EU ${eu} is the inner circumference of ${bore}, in mm.` };
    }
    case "UK": {
      const row = UK_RING_SIZES.reduce((best, r) => (Math.abs(r.diameterMM - dia) < Math.abs(best.diameterMM - dia) ? r : best));
      return {
        size: row.label.replace("½", ".5"),
        note: `UK ${row.label} (Ø ${row.diameterMM.toFixed(2)}) is the letter nearest ${bore}.`,
      };
    }
    case "IN":
      return { size: null, note: `The check finds the Indian size nearest ${bore}.` };
  }
}

/**
 * The viewer's size note (`CncExportResult.notes`, `"size: …"`) as a sentence
 * to show on its own. The viewer writes a clause that already names the size
 * ("UK V (Ø 20.39) is the letter nearest the ring's 20.40 mm bore"); it only
 * needs its full stop. Never prefix it with "This is" — that is how the
 * dialog once printed "This is UK V … is the letter nearest".
 */
export function cncSizeSentence(viewerNote: string): string {
  const t = viewerNote.trim().replace(/^size:\s*/, "");
  if (!t) return "";
  const s = t[0].toUpperCase() + t.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/**
 * A duration for people: whole seconds under a minute (a 2-second drilling
 * cycle must not read "0.0 min"), "under 1 s" below that, else minutes to one
 * decimal.
 */
export function formatCncDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0 s";
  const seconds = minutes * 60;
  if (seconds < 1) return "under 1 s";
  if (seconds < 59.5) return `${Math.round(seconds)} s`;
  return `${minutes.toFixed(1)} min`;
}

/**
 * Splits a check id off the front of a message: `"[profile.calibrated] …"`
 * (an older viewer's warnings) or `"cam3dm.unavailable: …"` (a planning
 * note). The dialog shows the message and keeps the id small and secondary —
 * an id is for whoever reads the setup sheet's check list, not opening words.
 */
export function splitCheckId(text: string): { id: string | null; message: string } {
  const bracket = /^\[([A-Za-z][\w.-]*)\]\s*/.exec(text);
  if (bracket) return { id: bracket[1], message: text.slice(bracket[0].length) };
  const dotted = /^([a-z][A-Za-z0-9]*(?:\.[A-Za-z0-9]+)+):\s+/.exec(text);
  if (dotted) return { id: dotted[1], message: text.slice(dotted[0].length) };
  return { id: null, message: text };
}

/**
 * `result.warnings` without the ones the dialog already lists as checks: the
 * viewer adds every warn check's message to `warnings` for the setup sheet,
 * so showing both printed each warning twice. Likewise the export's own
 * "UNCALIBRATED: …" line, once the `profile.calibrated` warn is listed: the
 * two say the same thing in nearly the same words.
 */
export function machinistNotes(warnings: readonly string[], warnChecks: readonly { message: string; id?: string }[]): string[] {
  const listed = new Set(warnChecks.map((c) => c.message.trim()));
  const calibrationListed = warnChecks.some((c) => c.id === "profile.calibrated");
  return warnings.filter((w) => {
    const message = splitCheckId(w).message.trim();
    return !listed.has(message) && !(calibrationListed && /^UNCALIBRATED\b/.test(message));
  });
}

/** Plain 7-bit text for a file a shop may open anywhere (the batch manifest). */
export function asciiText(text: string): string {
  const table: Record<string, string> = {
    "Ø": "dia", "ø": "dia", "°": " deg", "—": "-", "–": "-", "−": "-", "→": "->", "≥": ">=", "≤": "<=",
    "±": "+/-", "×": "x", "½": ".5", "¼": ".25", "¾": ".75", "θ": "theta", "µ": "u", "‘": "'", "’": "'", "“": "\"", "”": "\"", "…": "...",
  };
  return text
    .replace(/[Øø°—–−→≥≤±×½¼¾θµ‘’“”…]/g, (c) => table[c] ?? "?")
    .replace(/\bdia(?=[\d.])/g, "dia ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x09\x0a\x0d\x20-\x7e]/g, "?");
}

/**
 * Line 1 of the program, filled the way the post fills it
 * (`"%{LABEL} SZ{SIZE} GSMOD{MODEL} "` → `"%UGTH2310 SZ10.75 GSMOD368222 "`).
 * For showing the user; the viewer writes the real one.
 */
export function cncHeaderLine(template: string, header: { label: string; size: string; model: string }): string {
  return template.replace(/\{(LABEL|SIZE|MODEL)\}/g, (_, k: string) =>
    k === "LABEL" ? header.label : k === "SIZE" ? header.size : header.model,
  );
}

/**
 * The Size tab's US select, in quarter steps. Lives here (pure) so the CNC
 * check script can cover it.
 *
 * The catalogue lists whole and half sizes
 * only; the quarters between them (10.75 — the size the shop's CNC sample is
 * cut at) are real US sizes, and `setRingSize` already interpolates the bore
 * between the table's rows, so offering them needs no viewer change. A size
 * the state carries that is off this grid (an old design) is kept as its own
 * option so the select never shows a size the ring is not.
 */
export function usQuarterSizes(catalogSizes: readonly number[], current: number): number[] {
  if (catalogSizes.length === 0) return [current];
  const lo = Math.min(...catalogSizes);
  const hi = Math.max(...catalogSizes);
  const out: number[] = [];
  for (let q = Math.ceil(lo * 4); q <= Math.floor(hi * 4); q++) out.push(q / 4);
  if (!out.includes(current)) out.push(current);
  return out.sort((a, b) => a - b);
}

/** One operation of a program, as its comment line names it. */
export interface CncOperation {
  /** The comment text without its parentheses, double spaces kept. */
  text: string;
  /** How many times in a row the program runs it. */
  count: number;
}

/**
 * The operations a program runs, read off its TEXT: every `( … )` comment line
 * after the first tool change, in order, with consecutive repeats folded into a
 * count (the shop's sample runs `SIDE STEP` four times in a row). Comments
 * before the first `M6` are header furniture (the sample's `(TOOL - 0 DIA. …)`)
 * and are skipped.
 *
 * Read from the text the gate re-parsed, never from a planner's own list, so
 * what the dialog says will be machined is what the file says.
 */
export function programOperations(nc: string): CncOperation[] {
  const ops: CncOperation[] = [];
  let pastFirstToolChange = false;
  for (const raw of nc.split(/\r?\n/)) {
    const line = raw.trim();
    if (!pastFirstToolChange) {
      if (/^M0?6\b|^M0?6T|T\d+M0?6/.test(line)) pastFirstToolChange = true;
      continue;
    }
    const m = /^\((.*)\)$/.exec(line);
    if (!m) continue;
    const last = ops[ops.length - 1];
    if (last && last.text === m[1]) last.count++;
    else ops.push({ text: m[1], count: 1 });
  }
  return ops;
}
